const post = {
  slug: 'script-pacing-and-shot-length',
  cluster: 'scripting',
  eyebrow: 'Scripting',
  title: 'Sentence length is shot length',
  description:
    'Why a well-written script can still feel monotonous on screen, and how to fix pacing in the writing rather than the edit.',
  keywords: [
    'script pacing', 'video pacing', 'shot length', 'editing rhythm',
    'sentence length', 'video rhythm', 'script rhythm',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/script-pacing-and-shot-length.webp',
    alt: 'A row of sentences of varying length above a matching strip of shots, short sentences aligning with quick cuts.',
  },
  lede:
    'If your scenes cut roughly at sentence boundaries — and most editing approaches land close to that — then the rhythm of your prose becomes the rhythm of your edit. That link is why a good script can still feel flat on screen.',
  sections: [
    {
      heading: 'The mechanism',
      body: [
        {
          type: 'para',
          text: 'Video is cut at natural breaks in the narration, because cutting mid-clause looks like a mistake. A sentence therefore tends to correspond to a shot.',
        },
        {
          type: 'para',
          text: 'Which means: a run of six similar-length sentences produces six similar-length shots. The prose can be excellent and the result still reads as monotonous, because the *visual* rhythm never varies. Editors experience this as "the footage is boring" and try to fix it with more cuts, which does not work because the problem is upstream.',
        },
      ],
    },
    {
      heading: 'What to vary and where',
      body: [
        {
          type: 'para',
          text: 'Two registers, used deliberately.',
        },
        {
          type: 'list',
          items: [
            '**Short sentences and fragments** become hard cuts. Use them at reveals, at turns, and on the beat right after a number lands.',
            '**Longer sentences** become held shots. Use them for mechanism and explanation, where the viewer needs a moment on one image anyway.',
          ],
        },
        {
          type: 'para',
          text: 'Two working rules: never put two long sentences next to each other, and break any run of three at similar length. Those two alone fix most of it.',
        },
        {
          type: 'example',
          bad: 'The engineers had identified the problem in testing, and they had submitted a report recommending a delay, but the launch window was narrow and the decision was made to proceed on schedule despite the objections.',
          good: 'The engineers found it in testing. They wrote a report. They recommended a delay — and the launch window was narrow, the schedule was public, and the decision was made to go anyway.',
          note: 'Four shots instead of one, and the short ones land where the tension is.',
        },
      ],
    },
    {
      heading: 'Pacing is not speed',
      body: [
        {
          type: 'para',
          text: 'A common misreading: pacing means going faster. It does not — uniformly fast is as monotonous as uniformly slow, just more tiring.',
        },
        {
          type: 'para',
          text: 'Pacing is *variation*. A held shot only feels held because it follows quick ones. A hard cut only lands because something slower preceded it. Contrast does the work, and contrast requires both registers to be present.',
        },
      ],
    },
    {
      heading: 'The beat after a number',
      body: [
        {
          type: 'para',
          text: 'Worth its own note, because it is the most reliable single application of this.',
        },
        {
          type: 'para',
          text: 'When you state a figure that is supposed to be surprising, the next sentence should be short. It gives the number room to register, and it produces a cut immediately after the number appears on screen — which is where you want the visual emphasis anyway.',
        },
        {
          type: 'para',
          text: 'Bury the figure mid-paragraph followed by another long sentence and it passes unnoticed, no matter how striking it is.',
        },
      ],
    },
    {
      heading: 'Checking it',
      body: [
        {
          type: 'para',
          text: 'Read the draft and mark each sentence long, medium or short. Then look at the pattern rather than the words.',
        },
        {
          type: 'para',
          text: 'Long runs of one marking are where the video will drag. A stretch of unbroken medium is the most common and the hardest to notice by reading, because nothing about it is individually wrong.',
        },
        {
          type: 'callout',
          title: 'Why this matters more without a presenter',
          text: 'An on-camera presenter varies pace with delivery — pauses, emphasis, energy. Narration over footage has far less of that available, so more of the rhythm has to come from the writing itself.',
        },
      ],
    },
  ],
  related: ['writing-a-script-for-narration', 'youtube-script-writing', 'youtube-video-script-structure'],
};

export default post;
